import React, { useEffect, useState } from "react";
import { X, MonitorSmartphone } from "lucide-react";

/**
 * Iter-416 — till install banner ("Add Till to home screen").
 * - Android/Chrome: captures `beforeinstallprompt` and fires it from
 *   the button (the browser only offers the event on installable
 *   pages, i.e. once the manifest + icons are served).
 * - iOS Safari: no install prompt API — show Share → Add to Home
 *   Screen instructions instead.
 * - Hidden when already running standalone (installed) or after the
 *   staff dismissed it (persisted on the device).
 */

const DISMISS_KEY = "till_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export default function TillInstallBanner() {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "1",
  );
  const [installEvt, setInstallEvt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvt(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch { /* private mode */ }
    setDismissed(true);
  };

  const ios = isIos();
  if (installed || dismissed || (!installEvt && !ios)) return null;

  const install = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    try {
      const { outcome } = await installEvt.userChoice;
      if (outcome === "accepted") setInstalled(true);
    } catch { /* prompt already used */ }
    setInstallEvt(null);
    dismiss();
  };

  return (
    <div
      data-testid="till-install-banner"
      className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 flex items-start gap-3"
    >
      <div className="shrink-0 w-9 h-9 rounded-xl bg-white border border-emerald-200 grid place-items-center text-emerald-700">
        <MonitorSmartphone className="w-4.5 h-4.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-slate-900">Keep the till one tap away</div>
        {ios && !installEvt ? (
          <p className="mt-0.5 text-xs text-slate-600" data-testid="till-install-ios-help">
            Tap <span className="font-bold">Share</span> in Safari, then{" "}
            <span className="font-bold">Add to Home Screen</span> — the till opens full-screen like an app.
          </p>
        ) : (
          <>
            <p className="mt-0.5 text-xs text-slate-600">
              Install the till on this device so staff open it straight from the home screen.
            </p>
            <button
              type="button"
              data-testid="till-install-btn"
              onClick={install}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold px-4 py-1.5 transition-colors"
            >
              Add Till to home screen
            </button>
          </>
        )}
      </div>
      <button
        type="button"
        data-testid="till-install-dismiss"
        aria-label="Dismiss install banner"
        onClick={dismiss}
        className="shrink-0 text-slate-400 hover:text-slate-700 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
